import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  useMemo
} from 'react'
import AuftragOption, { type AuftragOptionProps } from './AuftragOption'
import { type FieldErrors, get as getRHFError } from 'react-hook-form'
import { RadioChipGroup, type RadioChipOption } from '../../form'
import { cn } from '../../../utils'
import ReadOnlyText from './ReadOnlyText'
import { useTranslation } from 'react-i18next'
import { useBefehlFormConfigIfAvailable } from './BefehlFormConfigContext'
import BorderedBox from './BorderedBox'

export interface AuftragOptionenProps {
  name: string
  value: string
  className?: string
  header?: ReactElement
  slideToUnlock?: ReactElement
  onChange: (value: string) => void
  readOnly?: boolean
  disabled?: boolean
  errors?: FieldErrors
  children: React.ReactNode
  'data-testid'?: string
}

export default function AuftragOptionen({
  name,
  value,
  onChange,
  header,
  slideToUnlock,
  className,
  readOnly = false,
  disabled = false,
  errors,
  children,
  'data-testid': testId = 'auftrag-optionen'
}: Readonly<AuftragOptionenProps>) {
  const { t } = useTranslation('ui-befehle')
  const formConfig = useBefehlFormConfigIfAvailable()

  const contextReadOnly = formConfig?.readOnly ?? readOnly
  const activateEmptyWarning = formConfig?.activateEmptyFieldWarnings ?? false

  const auftragOptionsAsChildren = Children.toArray(children).filter(
    (child): child is ReactElement<AuftragOptionProps> =>
      isValidElement(child) && child.type === AuftragOption
  )

  const selectedOptionElement = auftragOptionsAsChildren.find(
    (option) => option.props.value === value
  )

  const hasFormError = !!(name && errors && getRHFError(errors, name))

  const radioChipOptions = useMemo((): RadioChipOption[] => {
    return auftragOptionsAsChildren.map((child) => ({
      value: child.props.value,
      label: child.props.label,
      disabled: child.props.disabled,
      className: child.props.className,
      testId:
        child.props['data-testid'] ?? `${testId}-option-${child.props.value}`
    }))
  }, [auftragOptionsAsChildren, testId])

  return (
    <BorderedBox
      className={className}
      data-testid={testId}
      footer={
        <>
          <RadioChipGroup
            name={name}
            value={value}
            onChange={onChange}
            options={radioChipOptions}
            hasError={hasFormError}
            readOnly={contextReadOnly}
            disabled={disabled}
            showOnlySelected={contextReadOnly}
            activateEmptyWarning={activateEmptyWarning}
            testId={`${testId}-chip-group`}
            className="text-sm"
          />
          {slideToUnlock}
        </>
      }
    >
      <div data-testid={testId} className="flex flex-col">
        <div>
          {selectedOptionElement ? (
            cloneElement(selectedOptionElement, {
              ...selectedOptionElement.props,
              isSelected: true,
              hasError: selectedOptionElement.props.hasError ?? hasFormError,
              readOnly: contextReadOnly,
              'data-testid': `${testId}-selected-option-${selectedOptionElement.props.value}`
            } as AuftragOptionProps)
          ) : (
            <div className={cn('relative', { 'opacity-40': disabled })}>
              <ReadOnlyText
                value={contextReadOnly ? '' : t('please_select_option')}
                label=""
                variant="solid"
                activateEmptyWarning={activateEmptyWarning && !value}
                data-testid={`${testId}-placeholder`}
              />
            </div>
          )}
        </div>
        {header && <div>{header}</div>}
      </div>
    </BorderedBox>
  )
}
